/**
 * Step1Page - Critical Path Component (Layer A)
 * 
 * This component renders INSTANTLY as part of the critical bundle.
 * It contains only what's needed for the first paint:
 * - Header with logo
 * - Step 1 of 3 indicator
 * - "What best describes your situation?" headline
 * - 3 option buttons
 * - Footer trust bar
 * - Footer with copyright
 * 
 * All other quiz steps (2, 3, results, etc.) are lazy-loaded
 * via dynamic imports after user interaction.
 * 
 * Target: < 150KB compressed, FCP < 800ms on 4G
 */

import { useState, useEffect, lazy, Suspense } from "react";
import { Building2, ShieldCheck, ClipboardCheck } from "lucide-react";

// Lazy load the legal modal (not needed for initial render)
const LegalModal = lazy(() => import("../components/LegalModal"));

// ============================================================================
// INLINE COMPONENTS (no external dependencies)
// ============================================================================

const ProgressIndicator = ({ current, total }) => (
  <p className="text-sm text-gray-500 mb-4">Step {current} of {total}</p>
);

const OptionButton = ({ label, onClick, disabled }) => (
  <button
    onClick={onClick}
    disabled={disabled}
    className="w-full max-w-md px-6 py-4 text-left border-2 border-[#d0d4df] rounded-xl hover:border-[#2F6F73] hover:bg-[#f0fdf4] transition-all text-[#002C5F] font-medium disabled:opacity-50"
  >
    {label}
  </button>
);

// ============================================================================
// TRACKING UTILITIES (minimal, non-blocking)
// ============================================================================

const trackPostHog = (event, props = {}) => {
  if (typeof window !== 'undefined' && window.posthog) {
    window.posthog.capture(event, props);
  }
};

const trackClarity = (key, value) => {
  if (typeof window !== 'undefined' && window.clarityTrack) {
    window.clarityTrack.setTag(key, value);
  }
};

const trackMetaEvent = (eventName, params = {}) => {
  if (typeof window !== 'undefined' && window.fbq) {
    const eventId = `${eventName}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    window.fbq('track', eventName, params, { eventID: eventId });
  }
};

// ============================================================================
// UTM CAPTURE (runs once on mount)
// ============================================================================

const captureUTMParams = () => {
  if (typeof window === 'undefined') return {};
  
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || '',
    utm_medium: params.get('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || '',
    utm_content: params.get('utm_content') || '',
    utm_term: params.get('utm_term') || '',
    utm_adset: params.get('utm_adset') || '',
    fbclid: params.get('fbclid') || '',
    gclid: params.get('gclid') || '',
    msclkid: params.get('msclkid') || '',
    ttclid: params.get('ttclid') || '',
  };
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export default function Step1Page({ onSelectOption, isLoading }) {
  const [legalModalType, setLegalModalType] = useState(null);
  
  // Capture UTM params and track page view on mount
  useEffect(() => {
    const params = captureUTMParams();
    sessionStorage.setItem('utm_data_start', JSON.stringify(params));
    
    trackPostHog('start_page_view');
    trackClarity('funnel_step', 'landing');
    trackMetaEvent('PageView');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOptionClick = (choice) => {
    trackPostHog('quiz_q1', { choice });
    trackMetaEvent('QuizStarted', { quiz_path: choice });
    trackClarity('funnel_step', 'q1_situation');
    onSelectOption(choice);
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <picture>
            <source srcSet="/assets/logo-horizontal.webp" type="image/webp" />
            <img 
              src="/assets/logo-horizontal.png" 
              alt="Canadian Document Services" 
              width="120"
              height="40"
              className="h-10 w-auto object-contain"
              onError={(e) => { e.target.style.display = 'none'; }}
            />
          </picture>
          <span className="text-[#002C5F] font-semibold text-sm hidden sm:block">
            CANADIAN DOCUMENT SERVICES INC.
          </span>
        </div>
        <div className="bg-[#0f2a5c] text-white text-center py-2 text-sm">
          Administrative Intake Portal
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-4 py-8">
        <div className={`w-full max-w-lg transition-opacity duration-200 ${isLoading ? 'opacity-50' : 'opacity-100'}`}>
          <div className="text-center">
            <ProgressIndicator current={1} total={3} />
            <h1 className="text-2xl md:text-3xl font-bold text-[#002C5F] mb-8 font-serif">
              What best describes your situation?
            </h1>
            <div className="space-y-3 flex flex-col items-center">
              <OptionButton 
                label="I want to clear my Canadian criminal record"
                onClick={() => handleOptionClick('pardon')}
                disabled={isLoading}
              />
              <OptionButton 
                label="I'm concerned my record may affect U.S. travel"
                onClick={() => handleOptionClick('waiver')}
                disabled={isLoading}
              />
              <OptionButton 
                label="I'm just researching options"
                onClick={() => handleOptionClick('researcher')}
                disabled={isLoading}
              />
            </div>
          </div>
        </div>
      </main>

      {/* Trust Bar */}
      <div className="bg-gray-50 border-t border-gray-200 py-3 px-4">
        <div className="max-w-4xl mx-auto flex flex-wrap items-center justify-center gap-6 md:gap-8">
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <Building2 className="w-3.5 h-3.5 text-gray-400" />
            Federally Incorporated · Canada
          </span>
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <ShieldCheck className="w-3.5 h-3.5 text-gray-400" />
            No Filing Without Written Authorization
          </span>
          <span className="flex items-center gap-2 text-xs text-gray-500">
            <ClipboardCheck className="w-3.5 h-3.5 text-gray-400" />
            Free Assessment — No Obligation
          </span>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#0f2a5c] text-white py-4">
        <div className="max-w-4xl mx-auto px-4 flex flex-col md:flex-row items-center justify-between gap-2 text-sm">
          <p>© {new Date().getFullYear()} Canadian Document Services Inc.</p>
          <div className="flex gap-4">
            <button onClick={() => setLegalModalType('terms')} className="hover:underline">
              Terms & Conditions
            </button>
            <span>|</span>
            <button onClick={() => setLegalModalType('privacy')} className="hover:underline">
              Privacy Policy
            </button>
          </div>
        </div>
      </footer>

      {/* Legal Modal - Lazy loaded when opened */}
      {legalModalType && (
        <Suspense fallback={
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg p-8 max-w-2xl mx-4">
              <p className="text-gray-500">Loading...</p>
            </div>
          </div>
        }>
          <LegalModal 
            isOpen={true}
            onClose={() => setLegalModalType(null)}
            type={legalModalType}
          />
        </Suspense>
      )}
    </div>
  );
}
